// © 2001-2015 INOVx Solutions, Inc.
// 114 Pacifica Suite 300, Irvine, CA 92618 USA
// All rights reserved.
//
// This software is confidential, proprietary and unpublished property of
// INOVx Solutions, Inc.  It may NOT be copied in part or in whole on any
// medium, either electronic or printed, without the express written consent
// of INOVx Solutions, Inc.

import * as BABYLON from 'babylonjs';
import * as Interfaces from '../../Interfaces/Interfaces';
import * as Data from '../Data/WebData';
import * as Enumerations from '../../Enumerations/Enumerations';
import GLBConverter from '../../Utilities/GLBConverter';
import * as Inovx from '@shared';
import { VersionedArray } from '../../helpers/Helpers';
import MaterialManager from './MaterialManager';

class GLBManager implements Interfaces.Interfaces.IAssemblyManager {
    private _scene: BABYLON.Scene;
    // Blank scene, no camera, no lighting, purely used for loading glbs and getting meshes.
    private _loaderScene: BABYLON.Scene;
    private _glbConverter: GLBConverter;
    private _rayPickProvider: Interfaces.Interfaces.IRayPickProvider;
    private _eventManager: Interfaces.Interfaces.IEventManager;
    private _materialManager: MaterialManager;

    private _assembliesVisible: boolean = false;

    private _tempAssemblyCounter: number = 0;
    private _assemblyTemplates: Map<string, IAssemblyTemplate> = new Map();
    private _creationAssemblyID: string | null = null;

    private _assemblyRotation: number = 0;
    private _tempAssembly: BABYLON.TransformNode;
    //private _gizmoManager: BABYLON.GizmoManager;
    private _positionGizmo: BABYLON.PositionGizmo;
    private _rotationGizmo: BABYLON.RotationGizmo;
    private _scaleGizmo: BABYLON.BoundingBoxGizmo;
    private _currentAssemblyRotation: BABYLON.Vector3;
    private _selectedGLBKey: [string, string];
    private _pointerMoved = false;
    private _lastPointerPosition: BABYLON.Vector2 | null = null;
    private _moveThreshold: number = 5; // Threshold distance in pixels to consider a move significant.
    private _currentCursorStyle: string = "auto"; // Add a property to store the current cursor style
    private _canvasMouseLeaveHandler: (event: MouseEvent) => void;
    private _tempAssemblyLock: boolean = false;
    private _selectedMesh: { original: BABYLON.TransformNode, selection: BABYLON.TransformNode };
    public _glbAssemblies: { [assemblyID: string]: BABYLON.Mesh[]; } = {};
    public _graphicSources: Map<string, BABYLON.TransformNode> = new Map();
    public _glbAssemblyInstances: Map<string, BABYLON.TransformNode> = new Map();
    private _assemblyInstanceColors: Map<string, number | null> = new Map();
    private _assemblyInstanceTemplates: Map<string, string> = new Map();
    // Track active gizmo drags across all gizmo types so we only resume controls when all drags end
    private _activeGizmoDragCount: number = 0;
    private _meshes: VersionedArray<BABYLON.AbstractMesh> = new VersionedArray();

    constructor(eventManager: Interfaces.Interfaces.IEventManager, scene: BABYLON.Scene, materialManager: MaterialManager) {
        this._scene = scene;
        this._eventManager = eventManager;
        this._materialManager = materialManager;
        this._glbConverter = new GLBConverter();

        this._scene.onPointerObservable.add(this._handlePointerObservable);
        this._scene.onKeyboardObservable.add(this._handleKeyboardObservable);
        this._canvasMouseLeaveHandler = this._handleMouseLeave.bind(this);
        this._scene.getEngine().getRenderingCanvas().addEventListener("mouseleave", this._canvasMouseLeaveHandler);
    }

    // =========================
    // NEW TEMPLATE LIBRARY API
    // =========================
    public setAssemblyTemplates(templates: IAssemblyTemplate[]) {
        this._assemblyTemplates.clear();
        templates.forEach(t => this._assemblyTemplates.set(t.ID, t));
    }

    public upsertAssemblyTemplate(template: IAssemblyTemplate) {
        this._assemblyTemplates.set(template.ID, template);
    }

    private _getTemplate(assemblyID: string): IAssemblyTemplate | null {
        return this._assemblyTemplates.get(assemblyID) ?? null;
    }

    // =========================
    // NEW CREATION MODE API
    // =========================
    public beginAssemblyCreation(assemblyID: string) {
        this._disposeTempAssembly();
        this._assemblyRotation = 0;
        this._creationAssemblyID = assemblyID;
    }

    public endAssemblyCreation() {
        this._disposeTempAssembly();
        this._creationAssemblyID = null;
    }

    public isInCreationMode(): boolean {
        return this._creationAssemblyID != null;
    }

    private _getCreationTemplate(): IAssemblyTemplate | null {
        if (!this._creationAssemblyID) return null;
        return this._getTemplate(this._creationAssemblyID);
    }

    // =========================
    // DISPOSE
    // =========================
    public dispose() {
        // Remove observables
        this._scene.onPointerObservable.removeCallback(this._handlePointerObservable);
        this._scene.onKeyboardObservable.removeCallback(this._handleKeyboardObservable);

        const canvas = this._scene.getEngine().getRenderingCanvas();
        canvas.removeEventListener("mouseleave", this._canvasMouseLeaveHandler);

        // Dispose temp assembly
        this._disposeTempAssembly();

        this._glbAssemblyInstances.forEach((instance) => {
            this._disposeInstance(instance);
        });
        this._glbAssemblyInstances.clear();
        this._assemblyInstanceColors.clear();
        this._assemblyInstanceTemplates.clear();

        // Dispose all GLB assemblies and their instances
        for (const meshes of Object.values(this._glbAssemblies)) {
            meshes.forEach((mesh) => mesh.dispose());
        }
        this._glbAssemblies = {};

        for (const source of Object.values(this._graphicSources)) {
            source.dispose();
        }

        this._graphicSources.clear();

        // Dispose GLB Converter
        if (this._glbConverter) {
            this._glbConverter = null; // If GLBConverter has a dispose, call it
        }
    }

    public setRayPickProvider(rayPickProvider: Interfaces.Interfaces.IRayPickProvider) {
        this._rayPickProvider = rayPickProvider;
    }

    public hasAssembly(assemblyID: string, instanceID: string): boolean {
        if (this._glbAssemblies[assemblyID] != null && this._glbAssemblyInstances.has(instanceID))
            return true;
        return false;
    }

    public hasAssemblyInstance(instanceID: string): boolean {
        return this._glbAssemblyInstances.has(instanceID);
    }

    public setAssembliesVisible(visible: boolean): void {
        this._assembliesVisible = visible;
        this._glbAssemblyInstances.forEach((mesh) => {
            mesh.setEnabled(visible);
        });
    }

    public getAllMeshes(): VersionedArray<BABYLON.AbstractMesh> {
        return this._meshes;
    }

    // =========================
    // LOADING HELPERS
    // =========================
    private async _ensureAssemblyLoaded(assemblyID: string): Promise<boolean> {
        if (this._glbAssemblies[assemblyID] != null) return true;

        const tmpl = this._getTemplate(assemblyID);
        if (!tmpl) {
            console.warn(`No template found for assemblyID=${assemblyID}`);
            return false;
        }

        const url = tmpl.GraphicsFileUrl.startsWith("http")
            ? tmpl.GraphicsFileUrl
            : Inovx.Shared.Common.UrlUtilities.getBaseApplicationUrl() + tmpl.GraphicsFileUrl;

        await this.loadGLBAssembly(tmpl.ID, tmpl.Name, url);
        return true;
    }

    public async loadGLBAssembly(assemblyID: string, assemblyName: string, glbUrl: string) {
        if (this._glbAssemblies[assemblyID] == null) {
            const url = glbUrl.startsWith("http")
                ? glbUrl
                : Inovx.Shared.Common.UrlUtilities.getBaseApplicationUrl() + glbUrl;

            // Load the GLB and get the meshes
            var glbMeshes = await this._glbConverter.loadGLBUrlIntoScene(this._scene, assemblyName, url, false);
            glbMeshes.forEach(mesh => mesh.setEnabled(false));

            // Store the filtered meshes in the _glbAssemblies dictionary
            this._glbAssemblies[assemblyID] = glbMeshes as BABYLON.Mesh[];
        }
    }

    // =========================
    // GRAPHIC SOURCES
    // =========================
    public async loadGraphicsources(graphicSources: IGraphicSource[]) {
        graphicSources.forEach(source => this.loadGraphicSource(source.ID, source.Name, source.GraphicsFileUrl, source.Position, source.Rotation, source.Scale));
    }

    public async unloadGraphicSources(graphicSources: IGraphicSource[]) {
        graphicSources.forEach(source => this.unloadGraphicSource(source.ID, source.Name));
    }

    public async unloadGraphicSource(graphicSourceID: string, graphicSourceName: string) {
        if (this._graphicSources.has(graphicSourceID)) {
            console.log("Unloading graphic source " + graphicSourceName);
            this._graphicSources.get(graphicSourceID).dispose();
            this._graphicSources.delete(graphicSourceID);
        }
    }

    public async loadGraphicSource(graphicSourceID: string, graphicSourceName: string, glbUrl: string, position: IVector3D, rotation: number, scaling: number) {
        const graphicSourceURL = glbUrl.startsWith("http")
            ? glbUrl
            : Inovx.Shared.Common.UrlUtilities.getBaseApplicationUrl() + glbUrl;

        if (!this._graphicSources.has(graphicSourceID)) {
            console.log("Loading graphic source " + graphicSourceName);
            var glbMeshes = await this._glbConverter.loadGLBUrlIntoScene(this._scene, graphicSourceName, graphicSourceURL, false);

            var rootTransform = glbMeshes.find(mesh => mesh.name === "__root__");

            const transformParent = new BABYLON.TransformNode(graphicSourceID, this._scene);
            rootTransform.parent = transformParent;

            transformParent.scaling = new BABYLON.Vector3(scaling, scaling, scaling);
            transformParent.position = new BABYLON.Vector3(position.X, position.Y, position.Z);
            transformParent.rotation = new BABYLON.Vector3(0, rotation, 0);

            this._graphicSources.set(graphicSourceID, transformParent);
        }
    }

    // =========================
    // ASSEMBLY UPDATE/CREATE
    // =========================
    public async updateAllAssemblies(assemblies: IAssembly[]) {
        const validInstanceIDs = new Set(assemblies.map(assembly => assembly.ID));
        var selectedMeshID = "";
        if (this._selectedMesh != null) {
            const [parsedAssemblyID, _, parsedInstanceID] = JSON.parse(this._selectedMesh.original.id);
            selectedMeshID = parsedInstanceID;
        }

        this._glbAssemblyInstances.forEach((transformNode, instanceID) => {
            if (!validInstanceIDs.has(instanceID)) {
                if (instanceID == selectedMeshID)
                    this._unselectAssembly();

                this._disposeInstance(transformNode);
                this._glbAssemblyInstances.delete(instanceID);
                this._assemblyInstanceColors.delete(instanceID);
                this._assemblyInstanceTemplates.delete(instanceID);
            }
        });

        await Promise.all(assemblies.map(assembly => this.createOrUpdateAssembly(assembly)));
    }

    public async createOrUpdateAssembly(assembly: IAssembly) {
        var assemblyID = assembly.AssemblyTemplateID;
        var instanceID = assembly.ID;
        var position = new BABYLON.Vector3(assembly.Position.X, assembly.Position.Y, assembly.Position.Z);
        var orientation = new BABYLON.Vector3(assembly.Orientation.X, assembly.Orientation.Y, assembly.Orientation.Z);
        var scaling = new BABYLON.Vector3(assembly.InstanceScale.X, assembly.InstanceScale.Y, assembly.InstanceScale.Z);
        const color = assembly.Color ?? null;

        let mesh: BABYLON.TransformNode;
        const existing = this._glbAssemblyInstances.get(instanceID);
        const appearanceChanged = existing && (
            this._assemblyInstanceColors.get(instanceID) !== color
            || this._assemblyInstanceTemplates.get(instanceID) !== assemblyID
        );

        if (appearanceChanged) {
            const wasSelected = this.getSelectedAssemblyID() === instanceID;
            if (wasSelected) {
                this._unselectAssembly();
                this._selectedMesh = null;
            }
            this._disposeInstance(existing);
            this._glbAssemblyInstances.delete(instanceID);
            mesh = await this.createAssemblyInstanceWithHierarchy(assemblyID, instanceID, position, orientation, scaling, true, color);
            if (wasSelected && mesh) {
                this._selectAssembly(mesh);
            }
        } else if (!existing) {
            mesh = await this.createAssemblyInstanceWithHierarchy(assemblyID, instanceID, position, orientation, scaling, true, color);
        } else {
            mesh = this.updateAssembly(instanceID, position, orientation, scaling);
        }

        if(mesh)
            mesh.setEnabled(this._assembliesVisible);
    }

    private updateAssembly(instanceID: string,
        position: BABYLON.Vector3,
        orientation: BABYLON.Vector3,
        scaling: BABYLON.Vector3
    ) {
        var assembly = this._glbAssemblyInstances.get(instanceID);

        assembly.position = position;
        assembly.rotation = orientation;
        assembly.scaling = scaling;

        return assembly;
    }

    private async createAssemblyInstanceWithHierarchy(
        assemblyID: string,
        instanceID: string,
        position: BABYLON.Vector3,
        orientation: BABYLON.Vector3,
        scaling: BABYLON.Vector3,
        pickable: boolean,
        color: number | null = null
    ) {
        // When we are adding new instances, dispose the ghost.
        this._disposeTempAssembly();

        const ok = await this._ensureAssemblyLoaded(assemblyID);
        if (!ok) return null;

        const key = JSON.stringify([assemblyID, ':', instanceID]);

        if (this._glbAssemblyInstances.has(instanceID))
            return null;

        const originalRoot = this._glbAssemblies[assemblyID].find(mesh => mesh.name === "__root__");
        if (!originalRoot) return null;

        // Create a TransformNode as the root for positioning and scaling
        const rootTransform = new BABYLON.TransformNode(key, this._scene);

        // Recursive function to clone hierarchy
        const cloneHierarchy = (childrenToClone: BABYLON.Node[], parentInstance: BABYLON.Node | null) => {
            childrenToClone.forEach(originalMesh => {

                if (originalMesh instanceof BABYLON.Mesh) {
                    const instance = color == null
                        ? originalMesh.createInstance(`${instanceID}_${originalMesh.name}`)
                        : originalMesh.clone(`${instanceID}_${originalMesh.name}`, parentInstance, true);
                    if (!instance) return;
                    instance.parent = parentInstance;
                    if (color != null) {
                        instance.material = this._materialManager.getOrCreateHexMaterial(color);
                    }
                    instance.setEnabled(true);
                    instance.isPickable = pickable;
                    (instance as any).id = key;
                    instance.id = key;
                    cloneHierarchy(originalMesh.getChildren(), instance);
                    this._meshes.push(instance);
                } else if (originalMesh instanceof BABYLON.TransformNode) {
                    var transformNodeClone = originalMesh.clone(`${instanceID}_${originalMesh.name}`, parentInstance, true);
                    transformNodeClone.setEnabled(true);
                    cloneHierarchy(originalMesh.getChildren(), transformNodeClone);
                }
                else {
                    console.log("Unsupported Type");
                }
            });
        };

        cloneHierarchy(originalRoot.getChildren(), rootTransform);

        rootTransform.position = position;
        rootTransform.rotation = orientation;
        rootTransform.scaling = scaling;

        if (instanceID != "temp") {
            this._glbAssemblyInstances.set(instanceID, rootTransform);
            this._assemblyInstanceColors.set(instanceID, color);
            this._assemblyInstanceTemplates.set(instanceID, assemblyID);
        }

        return rootTransform;
    }

    public setSelectedAssemblyEdit() {
        this._unselectAssembly();
        this.setPositioningMode();
        this.setRotationMode();
    }
    public setSelectedAssemblyScale() {
        this._unselectAssembly();
        this.setScaleMode();
    }

    private setScaleMode() {
        if (this._selectedMesh == null)
            return;

        this._currentAssemblyRotation = this._selectedMesh.original.rotation.clone();
        var boundingBoxGizmo = new BABYLON.BoundingBoxGizmo(BABYLON.Color3.FromHexString("#0984e3"));

        boundingBoxGizmo.attachedMesh = this._selectedMesh.original as any;

        const draggerEnd = (() => {
            const [parsedAssemblyID, _, parsedInstanceID] = JSON.parse(this._selectedMesh.original.id);
            const assemblyData: Interfaces.Interfaces.IAssemblyUpdatedEvent = {
                ID: parsedInstanceID,
                Position: { X: this._selectedMesh.original.position.x, Y: this._selectedMesh.original.position.y, Z: this._selectedMesh.original.position.z },
                Orientation: { X: this._currentAssemblyRotation.x, Y: this._currentAssemblyRotation.y, Z: this._currentAssemblyRotation.z },
                InstanceScale: { X: this._selectedMesh.original.scaling.x, Y: this._selectedMesh.original.scaling.y, Z: this._selectedMesh.original.scaling.z }
            };
            this._eventManager.fireEvent(Enumerations.EventType.ASSEMBLYUPDATED, assemblyData);
        });

        // Camera suspension wrappers for scale drag
        let scaleStarted = false;
        const scaleDragEnd = () => {
            draggerEnd();
            this._activeGizmoDragCount = Math.max(0, this._activeGizmoDragCount - 1);
            scaleStarted = false;
            this._resumeCameraControlsIfIdle();
        };
        boundingBoxGizmo.onScaleBoxDragEndObservable.add(scaleDragEnd);
        boundingBoxGizmo.setEnabledRotationAxis("");
        boundingBoxGizmo.scaleDragSpeed = 1.5;

        boundingBoxGizmo.onScaleBoxDragObservable.add(() => {
            if (!scaleStarted) {
                scaleStarted = true;
                if (this._activeGizmoDragCount++ === 0) this._suspendCameraControls();
            }
            this._selectedMesh.original.scaling.x = Math.max(0.001, this._selectedMesh.original.scaling.x);
            this._selectedMesh.original.scaling.y = Math.max(0.001, this._selectedMesh.original.scaling.y);
            this._selectedMesh.original.scaling.z = Math.max(0.001, this._selectedMesh.original.scaling.z);
        });

        this._scaleGizmo = boundingBoxGizmo;
    }

    private setPositioningMode() {
        if (this._selectedMesh == null)
            return;

        var that = this;
        var gizmo = new BABYLON.PositionGizmo();

        gizmo.attachedMesh = this._selectedMesh.original as any;
        const draggerEnd = (() => {
            if (that._rotationGizmo != null) {
                that._rotationGizmo.yGizmo.isEnabled = true;
            }

            const [parsedAssemblyID, _, parsedInstanceID] = JSON.parse(this._selectedMesh.original.id);
            const assemblyData: Interfaces.Interfaces.IAssemblyUpdatedEvent = {
                ID: parsedInstanceID,
                Position: { X: this._selectedMesh.original.position.x, Y: this._selectedMesh.original.position.y, Z: this._selectedMesh.original.position.z },
                Orientation: { X: this._selectedMesh.original.rotation.x, Y: this._selectedMesh.original.rotation.y, Z: this._selectedMesh.original.rotation.z },
                InstanceScale: { X: this._selectedMesh.original.scaling.x, Y: this._selectedMesh.original.scaling.y, Z: this._selectedMesh.original.scaling.z }
            };
            this._eventManager.fireEvent(Enumerations.EventType.ASSEMBLYUPDATED, assemblyData);
        });
        const draggerStart = (() => {
            if (that._rotationGizmo != null) {
                that._rotationGizmo.yGizmo.isEnabled = false;
            }
        });

        const dragStartWrapper = () => {
            if (this._activeGizmoDragCount++ === 0) this._suspendCameraControls();
            draggerStart();
        };
        const dragEndWrapper = () => {
            draggerEnd();
            this._activeGizmoDragCount = Math.max(0, this._activeGizmoDragCount - 1);
            this._resumeCameraControlsIfIdle();
        };

        gizmo.xGizmo.dragBehavior.onDragEndObservable.add(dragEndWrapper);
        gizmo.yGizmo.dragBehavior.onDragEndObservable.add(dragEndWrapper);
        gizmo.zGizmo.dragBehavior.onDragEndObservable.add(dragEndWrapper);

        gizmo.xGizmo.dragBehavior.onDragStartObservable.add(dragStartWrapper);
        gizmo.yGizmo.dragBehavior.onDragStartObservable.add(dragStartWrapper);
        gizmo.zGizmo.dragBehavior.onDragStartObservable.add(dragStartWrapper);

        this._positionGizmo = gizmo;
    }

    private setRotationMode() {
        if (this._selectedMesh == null)
            return;

        var that = this;
        var gizmo = new BABYLON.RotationGizmo();

        gizmo.attachedMesh = this._selectedMesh.original as any;
        const draggerEnd = (() => {
            if (that._positionGizmo != null) {
                that._positionGizmo.xGizmo.isEnabled = true;
                that._positionGizmo.yGizmo.isEnabled = true;
                that._positionGizmo.zGizmo.isEnabled = true;
            }
            const [parsedAssemblyID, _, parsedInstanceID] = JSON.parse(this._selectedMesh.original.id);
            const assemblyData: Interfaces.Interfaces.IAssemblyUpdatedEvent = {
                ID: parsedInstanceID,
                Position: { X: this._selectedMesh.original.position.x, Y: this._selectedMesh.original.position.y, Z: this._selectedMesh.original.position.z },
                Orientation: { X: this._selectedMesh.original.rotation.x, Y: this._selectedMesh.original.rotation.y, Z: this._selectedMesh.original.rotation.z },
                InstanceScale: { X: this._selectedMesh.original.scaling.x, Y: this._selectedMesh.original.scaling.y, Z: this._selectedMesh.original.scaling.z }
            };
            this._eventManager.fireEvent(Enumerations.EventType.ASSEMBLYUPDATED, assemblyData);
        });

        const draggerStart = (() => {
            if (that._positionGizmo != null) {
                that._positionGizmo.xGizmo.isEnabled = false;
                that._positionGizmo.yGizmo.isEnabled = false;
                that._positionGizmo.zGizmo.isEnabled = false;
            }
        });

        gizmo.xGizmo.isEnabled = false;
        gizmo.zGizmo.isEnabled = false;
        gizmo.yGizmo.isEnabled = true;
        const rotDragStart = () => {
            if (this._activeGizmoDragCount++ === 0) this._suspendCameraControls();
            draggerStart();
        };
        const rotDragEnd = () => {
            draggerEnd();
            this._activeGizmoDragCount = Math.max(0, this._activeGizmoDragCount - 1);
            this._resumeCameraControlsIfIdle();
        };
        gizmo.yGizmo.dragBehavior.onDragEndObservable.add(rotDragEnd);
        gizmo.yGizmo.dragBehavior.onDragStartObservable.add(rotDragStart);
        gizmo.scaleRatio = 1.5;
        gizmo.updateGizmoRotationToMatchAttachedMesh = false;
        this._rotationGizmo = gizmo;
    }

    private _disposeTempAssembly() {
        if (this._tempAssembly != null) {
            this._disposeInstance(this._tempAssembly);
            this._tempAssembly = null;
        }
    }

    private _disposeInstance(instance: BABYLON.TransformNode) {
        instance.getChildMeshes().forEach(mesh => {
            mesh.dispose();
            this._meshes.fastRemove(mesh);
        });
        instance.dispose(false);
    }

    private _unselectAssembly(): void {
        if (this._selectedMesh) {
            this._selectedMesh.original.setEnabled(this._assembliesVisible);
            this._disposeInstance(this._selectedMesh.selection);
        }
        if (this._positionGizmo) {
            this._positionGizmo.dispose();
            this._positionGizmo = null;
        }
        if (this._rotationGizmo) {
            this._rotationGizmo.dispose();
            this._rotationGizmo = null;
        }
        if (this._scaleGizmo) {
            this._scaleGizmo.dispose();
            this._scaleGizmo = null;
        }
        // Do NOT clear selection reference here as it is needed for draggers
        // this._selectedMesh = null;
    }

    public selectAssembly(assemblyID: string): void {
        this._unselectAssembly();
        this._selectedMesh = null;

        if (this._glbAssemblyInstances.has(assemblyID)) {
            this._selectAssembly(this._glbAssemblyInstances.get(assemblyID));
        }
    }

    public getSelectedAssemblyID(): string {
        if (this._selectedMesh) {
            if (this._selectedMesh.original.id.indexOf(':') < 0)
                return null;

            const [parsedAssemblyID, _, parsedInstanceID] = JSON.parse(this._selectedMesh.original.id);
            return parsedInstanceID;
        }

        return null;
    }

    public isSelectedAssembly(id: string) {
        return this._selectedMesh?.selection.id == id;
    }

    public getSelectedAssemblyBounds(assemblyID: string) {
        var root = this._glbAssemblyInstances.get(assemblyID);

        const bounds = root.getHierarchyBoundingVectors(true);
        const scaling = root.scaling;

        return {
            min: { X: bounds.min.x, Y: bounds.min.y, Z: bounds.min.z },
            max: { X: bounds.max.x, Y: bounds.max.y, Z: bounds.max.z },
            scaling: { X: scaling.x, Y: scaling.y, Z: scaling.z }
        };
    }

    private _selectAssembly(mesh: BABYLON.TransformNode): void {
        const key = mesh.id;

        if (key.indexOf(':') < 0)
            return;

        const [parsedAssemblyID, _, parsedInstanceID] = JSON.parse(key);

        const originalRoot = this._glbAssemblies[parsedAssemblyID]?.find(node => node.name === "__root__");
        if (!originalRoot) {
            console.warn(`Original root not found for key: ${key}`);
            return;
        }

        const rootTransform = mesh.clone(key, null, true) as BABYLON.TransformNode;

        const cloneHierarchy = (childrenToClone: BABYLON.Node[], parentInstance: BABYLON.Node | null): void => {
            childrenToClone.forEach(originalNode => {
                if (originalNode instanceof BABYLON.Mesh) {
                    const instance = originalNode.clone(`highlightclone`);
                    if (!instance) return;
                    instance.parent = parentInstance;
                    instance.setEnabled(true);
                    instance.isPickable = true;
                    (instance as any).id = key;

                    const sourceMaterialId = originalNode.material?.id || originalNode.material?.name || "DefaultMaterial";
                    const selectionMaterialName = this._materialManager.getSelectionBackfaceMaterialName(sourceMaterialId);
                    instance.material = this._materialManager.getMaterial(selectionMaterialName);

                    cloneHierarchy(originalNode.getChildren(), instance);
                    this._meshes.push(instance);
                } else if (originalNode instanceof BABYLON.TransformNode) {
                    const transformNodeClone = originalNode.clone(`highlightclone`, parentInstance, true);
                    transformNodeClone.setEnabled(true);
                    cloneHierarchy(originalNode.getChildren(), transformNodeClone);
                } else {
                    console.warn("Unsupported node type:", originalNode);
                }
            });
        };

        cloneHierarchy(originalRoot.getChildren(), rootTransform);

        mesh.setEnabled(false);

        this._selectedMesh = { original: mesh, selection: rootTransform };
    }

    private _updateCursorStyle(style: string) {
        const canvas = this._scene.getEngine().getRenderingCanvas();
        this._currentCursorStyle = style;
        canvas.style.cursor = style;
    }

    private _handleMouseLeave(event: MouseEvent) {
        this._disposeTempAssembly();
    }

    private _handleKeyboardObservable = async (evt: BABYLON.KeyboardInfo) => {
        switch (evt.type) {
            case BABYLON.KeyboardEventTypes.KEYDOWN:
                switch (evt.event.code) {
                    case "KeyQ":
                        if (this._tempAssembly != null) {
                            this._tempAssembly.rotation.y -= .05;
                            this._assemblyRotation = this._tempAssembly.rotation.y;
                        }
                        break;
                    case "KeyW":
                        if (this._tempAssembly != null) {
                            this._tempAssembly.rotation.y += .05;
                            this._assemblyRotation = this._tempAssembly.rotation.y;
                        }
                        break;
                }
                break;
        }
    };

    private _handlePointerObservable = async (evt: BABYLON.PointerInfo) => {
        const canvas = this._scene.getEngine().getRenderingCanvas();

        switch (evt.type) {
            case BABYLON.PointerEventTypes.POINTERDOWN:
                this._pointerMoved = false;
                const downPosition = new BABYLON.Vector2(evt.event.clientX, evt.event.clientY);
                this._lastPointerPosition = downPosition;
                break;

            case BABYLON.PointerEventTypes.POINTERUP:
                if (this._pointerMoved) {
                    break;
                }

                // Creation click
                if (this._creationAssemblyID != null && this._tempAssembly != null) {
                    const tmpl = this._getTemplate(this._creationAssemblyID);
                    if (!tmpl) break;

                    const assemblyData: Interfaces.Interfaces.IAssemblyCreatedEvent = {
                        Name: tmpl.Name,
                        AssemblyTemplateID: tmpl.ID,
                        Position: { X: this._tempAssembly.position.x, Y: this._tempAssembly.position.y, Z: this._tempAssembly.position.z },
                        Orientation: { X: this._tempAssembly.rotation.x, Y: this._tempAssembly.rotation.y, Z: this._tempAssembly.rotation.z },
                        InstanceScale: { X: this._tempAssembly.scaling.x, Y: this._tempAssembly.scaling.y, Z: this._tempAssembly.scaling.z }
                    };
                    this._eventManager.fireEvent(Enumerations.EventType.ASSEMBLYCREATED, assemblyData);

                    canvas.style.cursor = 'auto';
                }
                break;

            case BABYLON.PointerEventTypes.POINTERMOVE:

                if (this._lastPointerPosition) {
                    const currentPosition = new BABYLON.Vector2(evt.event.clientX, evt.event.clientY);
                    const distance = BABYLON.Vector2.Distance(this._lastPointerPosition, currentPosition);

                    if (distance > this._moveThreshold) {
                        this._pointerMoved = true;
                    }
                }

                if (this._creationAssemblyID == null) return;

                const tmpl = this._getTemplate(this._creationAssemblyID);
                if (!tmpl) return;

                this._scene.getEngine().getRenderingCanvas().focus();
                const canvasRect = this._scene.getEngine().getRenderingCanvasClientRect();
                const x = evt.event.clientX - canvasRect.left;
                const y = evt.event.clientY - canvasRect.top;
                const screenPoint = new BABYLON.Vector2(x, y);

                const intersection = this._rayPickProvider.rayPick(screenPoint.x, screenPoint.y);

                if (intersection.valid) {

                    const origin = new BABYLON.Vector3(
                        tmpl.Origin.X,
                        tmpl.Origin.Y,
                        tmpl.Origin.Z
                    );

                    if (!this._tempAssembly && !this._tempAssemblyLock) {
                        this._tempAssemblyLock = true;
                        try {
                            const temporaryAssembly = await this.createAssemblyInstanceWithHierarchy(
                                tmpl.ID,
                                'temp',
                                intersection.point.subtract(origin),
                                new BABYLON.Vector3(0, this._assemblyRotation, 0),
                                new BABYLON.Vector3(tmpl.Scale, tmpl.Scale, tmpl.Scale),
                                false
                            );
                            this._tempAssembly = temporaryAssembly;
                        } catch (error) {
                            console.error('Error creating tempAssembly:', error);
                        } finally {
                            this._tempAssemblyLock = false;
                        }
                    }

                    if (this._tempAssembly != null)
                        this._tempAssembly.position = intersection.point.subtract(origin);

                    this._updateCursorStyle(
                        "url('" +
                        Inovx.Shared.Common.UrlUtilities.getBaseApplicationUrl() +
                        "/cursors/validAssembly.png" +
                        "'), auto"
                    );
                } else {
                    this._disposeTempAssembly();
                    this._updateCursorStyle(
                        "url('" +
                        Inovx.Shared.Common.UrlUtilities.getBaseApplicationUrl() +
                        "/cursors/invalidAssembly.png" +
                        "'), auto"
                    );
                }
                break;
        }
    };

    private _suspendCameraControls() {
        const cam = this._scene.activeCamera as any;
        if (cam && typeof cam.setControlsSuspended === 'function') {
            cam.setControlsSuspended(true);
        }
    }

    private _resumeCameraControlsIfIdle() {
        if (this._activeGizmoDragCount === 0) {
            const cam = this._scene.activeCamera as any;
            if (cam && typeof cam.setControlsSuspended === 'function') {
                cam.setControlsSuspended(false);
            }
        }
    }
}

export default GLBManager;
